import { useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Container,
  Grid,
} from '@mui/material';
import { extractOriginPoints, extractComponentColors } from '@hashatar/core';
import AnimatedAvatar from '../AnimatedAvatar';
import HashDisplay from '../HashDisplay';

interface HowItWorksDemoProps {
  className?: string;
}

export default function HowItWorksDemo({ className }: HowItWorksDemoProps) {
  const avatarRef2 = useRef<HTMLDivElement>(null);
  const avatarRef3 = useRef<HTMLDivElement>(null);

  const [exampleText, setExampleText] = useState('john.doe@example.com');
  const hashatarSize = 64;

  return (
    <Box className={className}>
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          component="h2"
          sx={{
            textAlign: 'center',
            mb: 3,
            fontWeight: 'bold',
            color: '#333',
            fontSize: { xs: '2rem', md: '2.5rem' },
          }}
        >
          How It Works
        </Typography>

        <Typography
          variant="h6"
          component="p"
          sx={{
            textAlign: 'center',
            mb: 8,
            color: '#666',
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
          }}
        >
          Hashatar transforms any text into a unique visual pattern through three key steps
        </Typography>

        {/* Section 1: Basic hashatar with input - Left side */}
        <Grid container spacing={{ xs: 3, md: 6 }} alignItems="flex-start" sx={{ mb: { xs: 6, md: 12 } }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              {/* Input field */}
              <Box sx={{ width: { xs: 250, sm: 200 } }}>
                <TextField
                  fullWidth
                  label="Input Text"
                  value={exampleText}
                  onChange={(e) => setExampleText(e.target.value)}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                />
              </Box>
              
              {/* Hash Display */}
              <HashDisplay 
                text={exampleText} 
                size={{ xs: 160, sm: 180, md: 200 }}
                charsPerRow={8}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ pl: { md: 4 } }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  color: '#333',
                  mb: 3,
                }}
              >
                1. Generate Hash
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  lineHeight: 1.8,
                  fontSize: '1.1rem',
                }}
              >
                Any input text is processed through a cryptographic hash function to create a unique, 
                deterministic visual hash value. The same input will always produce the same hashatar, 
                making it perfect for consistent visual identification.
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Section 2: Same hashatar with points - Right side */}
        <Grid container spacing={{ xs: 3, md: 6 }} alignItems="flex-start" sx={{ mb: { xs: 6, md: 12 } }}>
          <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
            <Box sx={{ pr: { md: 4 } }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  color: '#333',
                  mb: 3,
                }}
              >
                2. Extract Origin Points
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  lineHeight: 1.8,
                  fontSize: '1.1rem',
                }}
              >
                A pseudorandom process is used to extract coordinate points from the hash value to serve as anchor points 
                for the visual structure. These origin points determine the fundamental geometry 
                and ensure consistent visual characteristics across different sizes.
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                sx={{
                  position: 'relative',
                  width: { xs: 160, sm: 180, md: 200 },
                  height: { xs: 160, sm: 180, md: 200 },
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '2px solid rgba(0, 0, 0, 0.1)',
                  backgroundColor: '#000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                ref={avatarRef2}
              >
                <AnimatedAvatar 
                  text={exampleText} 
                  size={hashatarSize}
                  containerRef={avatarRef2}
                  greyscale
                />
                
                {/* Origin Points Overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    pointerEvents: 'none',
                  }}
                >
                  {(() => {
                    try {
                      const points = extractOriginPoints(exampleText, hashatarSize);
                      return points.map((point, index) => (
                        <Box
                          key={index}
                          sx={{
                            position: 'absolute',
                            left: `${(point.x / hashatarSize) * 100}%`,
                            top: `${(point.y / hashatarSize) * 100}%`,
                            width: '8px',
                            height: '8px',
                            backgroundColor: '#ff0000',
                            borderRadius: '50%',
                            transform: 'translate(-50%, -50%)',
                            boxShadow: '0 0 10px rgba(255, 0, 0, 0.8)',
                            border: '2px solid #ffffff',
                          }}
                        />
                      ));
                    } catch (error) {
                      console.error('Error extracting origin points:', error);
                      return null;
                    }
                  })()}
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Section 3: Same hashatar with points and colors - Left side */}
        <Grid container spacing={{ xs: 3, md: 6 }} alignItems="flex-start">
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: { xs: 2, md: 4 }, flexDirection: { xs: 'column', sm: 'row' } }}>
              {/* Hashatar with points */}
              <Box
                sx={{
                  position: 'relative',
                  width: { xs: 160, sm: 180, md: 200 },
                  height: { xs: 160, sm: 180, md: 200 },
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '2px solid rgba(0, 0, 0, 0.1)',
                  backgroundColor: '#000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                ref={avatarRef3}
              >
                <AnimatedAvatar 
                  text={exampleText} 
                  size={hashatarSize}
                  containerRef={avatarRef3}
                />
                
                {/* Origin Points Overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    pointerEvents: 'none',
                  }}
                >
                  {(() => {
                    try {
                      const points = extractOriginPoints(exampleText, hashatarSize);
                      return points.map((point, index) => (
                        <Box
                          key={index}
                          sx={{
                            position: 'absolute',
                            left: `${(point.x / hashatarSize) * 100}%`,
                            top: `${(point.y / hashatarSize) * 100}%`,
                            width: '8px',
                            height: '8px',
                            backgroundColor: '#ff0000',
                            borderRadius: '50%',
                            transform: 'translate(-50%, -50%)',
                            boxShadow: '0 0 10px rgba(255, 0, 0, 0.8)',
                            border: '2px solid #ffffff',
                          }}
                        />
                      ));
                    } catch (error) {
                      console.error('Error extracting origin points:', error);
                      return null;
                    }
                  })()}
                </Box>
              </Box>

              {/* Component Colors */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'row', sm: 'column' },
                  height: { xs: 'auto', sm: 160, md: 200 },
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: 'space-between',
                  gap: { xs: 1, sm: 0 },
                }}
              >
                {(() => {
                  try {
                    const colors = extractComponentColors(exampleText, hashatarSize);
                    return colors.map((color, index) => (
                      <Box
                        key={index}
                        sx={{
                          width: '40px',
                          height: '40px',
                          backgroundColor: `rgb(${color[0]}, ${color[1]}, ${color[2]})`,
                          border: '2px solid rgba(0, 0, 0, 0.2)',
                          borderRadius: '4px',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                        }}
                      />
                    ));
                  } catch (error) {
                    console.error('Error extracting component colors:', error);
                    return null;
                  }
                })()}
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ pl: { md: 4 } }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  color: '#333',
                  mb: 3,
                }}
              >
                3. Apply Color Palette
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#666',
                  lineHeight: 1.8,
                  fontSize: '1.1rem',
                }}
              >
                A distinctive color palette is derived from the hash value using another pseudorandom process, creating vibrant and 
                unique color combinations. These colors are applied to different components of 
                the hashatar, resulting in a visually rich and instantly comparable avatar 
                that's both beautiful and functional.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
